import { useAxios } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { ImagePreviewModal } from '@/modules/shared/components/image-preview-modal/ImagePreviewModal'

type FileAnswerProps = {
  title?: string
  description?: string
  files?: {
    url: string
  }[]
}

export const FileAnswer = ({ title, description, files }: FileAnswerProps) => {
  const theme = useTheme()
  const axios = useAxios()
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({})

  const filesWithUrls = files?.filter((file) => file.url) || []

  useEffect(() => {
    const urls = (files?.filter((file) => file.url) || []).map((file) => file.url)

    if (urls.length === 0) {
      setBlobUrls({})
      return
    }

    let isCancelled = false
    const abortController = new AbortController()
    const objectUrls: string[] = []

    void Promise.all(
      urls.map(async (url) => {
        try {
          // SAS URLs are self-authenticated via query params; fetching via axios
          // adds an Authorization header that triggers a CORS preflight Azure
          // Blob Storage does not satisfy. Use the URL directly as the img src.
          if (url.includes('.blob.core.windows.net')) {
            return { url, objectUrl: url }
          }

          const response = await axios.get<Blob>(url, {
            responseType: 'blob',
            signal: abortController.signal,
          })
          const objectUrl = URL.createObjectURL(response.data)

          if (isCancelled) {
            URL.revokeObjectURL(objectUrl)
            return { url, objectUrl: null }
          }

          objectUrls.push(objectUrl)
          return { url, objectUrl }
        } catch {
          return { url, objectUrl: null }
        }
      }),
    ).then((results) => {
      if (!isCancelled) {
        setBlobUrls(
          Object.fromEntries(
            results
              .filter((r): r is { url: string; objectUrl: string } => r.objectUrl !== null)
              .map((r) => [r.url, r.objectUrl]),
          ),
        )
      }
    })

    return () => {
      isCancelled = true
      abortController.abort()
      objectUrls.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [axios, files])

  return (
    <FlexColumn gap={theme.UI.Spacing.Space2}>
      <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darkest}>
        {`${title && description ? `${title}: ` : title || ''}${description}`}
      </Span>
      {filesWithUrls.map((file, index) =>
        blobUrls[file.url] ? (
          <StyledImageButton
            key={index}
            type='button'
            aria-label={t({
              id: 'questionnaire.file-answer.open',
              comment: 'Label of an uploaded photo thumbnail that opens the photo full screen',
              message: 'Bekijk de foto die je opgeladen hebt',
            })}
            onClick={() => setSelectedImage(blobUrls[file.url])}
          >
            <StyledImage
              src={blobUrls[file.url]}
              alt={t({
                id: 'questionnaire.file-answer.photoAlt',
                comment: 'Alt text of a photo the patient uploaded',
                message: 'Foto die je opgeladen hebt',
              })}
            />
          </StyledImageButton>
        ) : null,
      )}

      <ImagePreviewModal isVisible={!!selectedImage} onClose={() => setSelectedImage(null)}>
        <ImagePreviewModal.Spinner />
        <ImagePreviewModal.Image
          src={selectedImage ?? undefined}
          alt={t({
            id: 'questionnaire.file-answer.photoAlt',
            comment: 'Alt text of a photo the patient uploaded',
            message: 'Foto die je opgeladen hebt',
          })}
        />
        <ImagePreviewModal.CloseButton onClick={() => setSelectedImage(null)} />
      </ImagePreviewModal>
    </FlexColumn>
  )
}

const StyledImageButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  background: none;
  cursor: pointer;
`

const StyledImage = styled.img`
  display: block;
  width: 100%;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
  height: 200px;
  object-fit: cover;
`
